import styles from "./popup-list.module.scss";
import { GrClose } from "react-icons/gr";
import ModalPopper from "@/components/layout/modal-popper/modal-popper";
import classNames from "classnames";
import LoadingIcon from "@/components/layout/loading-icon";
import SkeletonLoader from "@/components/layout/skeleton-loader/skeleton-loader";
import BlessSkeletonLoader from "@/components/layout/skeleton-loader-v2";
import { FaMagnifyingGlass } from "react-icons/fa6";

type Props<T> = {
  isOpen: boolean;
  listHeader?: string;
  list: T[] | undefined | null;
  item: (data: T) => React.ReactNode;
  activeIndex: number | string | undefined;
  onItemSelect: (item: T) => void;
  onClose: () => void;
  getId: (data: T) => string | number;
  activeId: number | string | undefined;
  isLoading?: boolean;
  noDataFallback?: React.ReactNode;
  shouldCloseOnSelect?: boolean;
};

function PopupList<T>({
  isOpen,
  listHeader,
  list,
  getId,
  activeId,
  item,
  onItemSelect,
  onClose,
  isLoading,
  noDataFallback,
  shouldCloseOnSelect = true,
}: Props<T>): JSX.Element | undefined {
  // Auto close when selected
  const handleItemSelect = (item: T) => {
    onItemSelect(item);
    if (shouldCloseOnSelect) onClose();
  };

  return (
    <ModalPopper isOpen={isOpen} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.headerSection}>
          {listHeader && <p className={styles.chainListHeader}>{listHeader}</p>}
        </div>
        <ul className={styles.list}>
          {isLoading ? (
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <BlessSkeletonLoader flex key={i} />
              ))}
            </>
          ) : (
            <>
              {list?.length ? (
                <>
                  {list.map((listItem) => {
                    const id = getId(listItem); // Extract the id from the list item
                    const isActive = activeId === id;
                    return (
                      <li
                        key={id}
                        role="button"
                        onClick={() => handleItemSelect(listItem)}
                        className={classNames(isActive && styles.active)}
                      >
                        {item(listItem)}
                      </li>
                    );
                  })}
                </>
              ) : (
                <div className={styles.noDataFound}>
                  <span>
                    <FaMagnifyingGlass size={70} />
                  </span>
                  {noDataFallback || "Sorry, we couldn’t find any results."}
                </div>
              )}
            </>
          )}
        </ul>
      </div>
    </ModalPopper>
  );
}

export default PopupList;
